import { NotificationKey } from '../enums/notification.enum';

export class ForecastProgressTracker {
  // ── Stage sequences ───────────────────────────────────────────────────────
  // Full ordered list of summaryStatus 'header' messages per company type,
  // covering both the summarize() waterfall and postProcess() steps.
  // Conditional stages (e.g. 'Applying warehouse qty', 'Calculating Advertising
  // Inventory') are included at their expected position — the cursor silently
  // skips them when they don't arrive. 'Aggregating Sales Metrics' appears twice
  // intentionally: once from the waterfall, once from postProcess; the cursor
  // advances to the next occurrence each time.
  // Source: forecast.job.server.js + process-forecast.job.server.js +
  //         summary.service.server.js + partial-summary.job.server.js
  private static readonly STAGE_SEQUENCES: Record<string, readonly string[]> = {
    ASC: [
      'Running Forecast Simulations',          // Phase 1: forecast.job.server.js
      'Processing Forecast Results',           // Phase 1: process-forecast.job.server.js
      'Hybridizing Forecast Results',
      'Summarize forecast results',
      'Cleaning broken Purchase As',
      'Applying Ordering Parameters',
      'Consolidating Item Dependencies',
      'Generating Purchase Recommendations',
      'Identifying Urgent Care Items',
      'Aggregating Sales Metrics',             // waterfall
      'Updating Out Of Stock Item',            // waterfall, ASC only
      'Aggregating Out Of Stock Item metrics', // waterfall, ASC only
      'Summary Item History Length ...',
      'Distributing Summary Jobs',
      'Calculating Sale Velocity',
      'Cleaning Up Restock Suggestions',
      'Updating Out Of Stock Items',           // postProcess (different from waterfall)
      'Aggregating Sales Metrics',             // postProcess
      'Generating Summaries',
      'Generating Summaries by vendor',
      'Aggregating Shipments',
      'Generating restock suggestions by vendor',
      'Calculating Advertising Inventory',     // ASC + Amazon Ads connected
      'Generating Price Recommendations',
      'Triggering Automation',                 // startForecastAfterSync only
    ],
    QBFS: [
      'Running Forecast Simulations',
      'Processing Forecast Results',
      'Hybridizing Forecast Results',
      'Summarize forecast results',
      'Cleaning broken Purchase As',
      'Applying Ordering Parameters',
      'Consolidating Item Dependencies',
      'Generating Purchase Recommendations',
      'Identifying Urgent Care Items',
      'Aggregating Sales Metrics',             // waterfall
      'Summary Item History Length ...',
      'Distributing Summary Jobs',
      'Calculating Sale Velocity',
      'Cleaning Up Restock Suggestions',
      'Aggregating Sales Metrics',             // postProcess
      'Generating Summaries',
      'Generating Summaries by vendor',
      'Generating restock suggestions by vendor',
    ],
    SFY: [
      'Running Forecast Simulations',
      'Processing Forecast Results',
      'Hybridizing Forecast Results',
      'Summarize forecast results',
      'Cleaning broken Purchase As',
      'Applying Ordering Parameters',
      'Consolidating Item Dependencies',
      'Generating Purchase Recommendations',
      'Identifying Urgent Care Items',
      'Aggregating Sales Metrics',             // waterfall
      'Summary Item History Length ...',
      'Distributing Summary Jobs',
      'Calculating Sale Velocity',
      'Cleaning Up Restock Suggestions',
      'Aggregating Sales Metrics',             // postProcess
      'Generating Summaries',
      'Generating Summaries by vendor',
      'Generating restock suggestions by vendor',
    ],
    SHIP_STATION: [
      'Running Forecast Simulations',
      'Processing Forecast Results',
      'Hybridizing Forecast Results',
      'Summarize forecast results',
      'Cleaning broken Purchase As',
      'Applying Ordering Parameters',
      'Consolidating Item Dependencies',
      'Generating Purchase Recommendations',
      'Identifying Urgent Care Items',
      'Aggregating Sales Metrics',             // waterfall
      'Summary Item History Length ...',
      'Distributing Summary Jobs',
      'Calculating Sale Velocity',
      'Cleaning Up Restock Suggestions',
      'Aggregating Sales Metrics',             // postProcess
      'Generating Summaries',
      'Generating Summaries by vendor',
      'Generating restock suggestions by vendor',
    ],
    default: [
      // CSV and any other types (no Sale Velocity / Restock Cleanup)
      'Running Forecast Simulations',
      'Processing Forecast Results',
      'Hybridizing Forecast Results',
      'Summarize forecast results',
      'Cleaning broken Purchase As',
      'Applying warehouse qty',               // CSV + !willDisableMultipleWarehouses
      'Applying Ordering Parameters',
      'Consolidating Item Dependencies',
      'Generating Purchase Recommendations',
      'Identifying Urgent Care Items',
      'Aggregating Sales Metrics',             // waterfall
      'Summary Item History Length ...',
      'Distributing Summary Jobs',
      'Aggregating Sales Metrics',             // postProcess
      'Generating Summaries',
      'Generating Summaries by vendor',
      'Generating restock suggestions by vendor',
    ],
  };

  progress = 0;
  private _stageCursor = 0;
  private _stageSequence: readonly string[] =
    ForecastProgressTracker.STAGE_SEQUENCES['default'];

  // Returns the number of Phase 2 stages completed. Used by nav-forecast to
  // detect whether Phase 2 has started (=== 0 means not yet started).
  get phase2StageCount(): number {
    return this._stageCursor;
  }

  setCompanyType(companyType: string): void {
    this._stageSequence =
      ForecastProgressTracker.STAGE_SEQUENCES[companyType] ??
      ForecastProgressTracker.STAGE_SEQUENCES['default'];
  }

  handleNotification(data: any): void {
    if (data?.key === NotificationKey.forecastStatus) this.onForecastStatus(data);
    if (data?.key === NotificationKey.summaryStatus) this.onSummaryStatus(data);
    // Note: 'close' and 'notice' do NOT reset — 'close' fires from each
    // individual partial-summary chunk, not just the final one. Resetting here
    // would drop the bar mid-run. Only 'forecastStatus title' resets.
  }

  private onForecastStatus(data: any): void {
    if (data.type === 'title') {
      this.reset();
      return;
    }
    // 'header' carries Phase 1 stage names — advance the cursor for tracking purposes
    // but do NOT modify progress. Phase 1 progress is driven entirely by the numeric
    // 'message' values (0-50%). Calling advanceToStage here would jump the bar to ~52%
    // before any percentage messages arrive.
    if (data.type === 'header') {
      const idx = this._stageSequence.findIndex((s, i) => i >= this._stageCursor && s === data.msg);
      if (idx !== -1) this._stageCursor = idx + 1;
      return;
    }
    if (data.type !== 'message') return;
    const parsed = Number.parseFloat(data.msg);
    // Only update progress if the new value would move the bar forward.
    if (parsed > 0) this.progress = Math.max(this.progress, Math.min(50, parsed * 0.5));
  }

  private onSummaryStatus(data: any): void {
    if (data.type === 'title') {
      // Non-empty msg is a queue/duplicate-job notification — ignore it.
      // Empty msg signals Phase 1→2 transition: set progress to 50% once
      // (progress < 50 guard). Subsequent titles from parallel partial-summary
      // chunks mid-run are ignored so the bar never goes backward.
      if (!data.msg && this.progress < 50) this.progress = 50;
      return;
    }
    if (data.type !== 'header') return;
    this.advanceToStage(data.msg);
  }

  private advanceToStage(name: string): void {
    // Find the next occurrence of this stage name at or after the current cursor.
    // Stages that arrive out of order (already passed) are silently skipped —
    // the bar never goes backward. Conditional stages absent from a run are
    // no-ops since findIndex returns -1. The backend publish() has a 500ms
    // setTimeout delay, so out-of-order Pusher delivery is expected.
    const idx = this._stageSequence.findIndex((s, i) => i >= this._stageCursor && s === name);
    if (idx === -1) return;
    this._stageCursor = idx + 1;
    this.progress = Math.min(100, 50 + (this._stageCursor / this._stageSequence.length) * 50);
  }

  private reset(): void {
    this.progress = 0;
    this._stageCursor = 0;
  }
}
